import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../../utils/notify';
import { useSnackbar } from '../../context/SnackbarContext';
import { getCustomerFrameworkDocumentApi, approveDocumentApi, getCustomizedFileBlobApi, approveCustomizedFileDocumentApi, rejectCustomizedFileDocumentApi } from '../../services/axios/AuthApis/TrustCenterApis';
import ApiLoader from '../ui/ApiLoader';
import DocumentPreviewModal from './DocumentPreviewModal';
import { DataTable } from '../common/DataTable';
import { FunnelIcon, MagnifyingGlassIcon, CheckBadgeIcon, DocumentTextIcon, ArrowPathIcon, EyeIcon, XCircleIcon } from '@heroicons/react/24/outline';

const PAGE_SIZE = 20;

const CustomizedFileApprovalView = ({ customer_id, userRole, frameworks }) => {
  const { showSnackbar } = useSnackbar();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, count: 0 });
  const [search, setSearch] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
  const [documentPreviewTitle, setDocumentPreviewTitle] = useState('');
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState('');
  const [documentPreviewFile, setDocumentPreviewFile] = useState(null); // downloaded file (Blob)
  const [previewScrollContainerRef] = useState(React.createRef());
  const [isPreviewScrollCompleted, setIsPreviewScrollCompleted] = useState(false);
  const [previewDocumentMeta, setPreviewDocumentMeta] = useState(null);
  const [previewPageCount, setPreviewPageCount] = useState(1);
  const [previewCurrentPage, setPreviewCurrentPage] = useState(1);
  const [selectedFileForApproval, setSelectedFileForApproval] = useState(null);
  const [pendingFilesToApprove, setPendingFilesToApprove] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const fetchData = useCallback(async (page = 1) => {
    if (!customer_id) {
      setRecords([]);
      return;
    }

    setLoading(true);
    try {
      const data = await getCustomerFrameworkDocumentApi(page, PAGE_SIZE, {
        customer_id,
        status: 'unapproved',
        search,
        framework_id: frameworkFilter,
        category: categoryFilter,
      });
      setRecords(data.results || []);
      setPagination({
        page: data.page || 1,
        total_pages: data.total_pages || 1,
        count: data.count || 0,
      });
    } catch (error) {
      console.error('Failed to load customized approvals:', error);
      showSnackbar('Failed to load pending customized documents.', 'error');
      setRecords([]);
      setPagination({ page: 1, total_pages: 1, count: 0 });
    } finally {
      setLoading(false);
    }
  }, [customer_id, search, frameworkFilter, categoryFilter, showSnackbar]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const formatFramework = (record) => {
    const names = record.framework_names || [];
    if (names.length > 0) return names.join(', ');
    return '—';
  };

  const getPendingFiles = (record) => {
    return (record.controls || []).flatMap((control) => {
      return (control.customized_document_file || []).filter((file) => file.status !== 'approved').map((file) => ({
        ...file,
        control_id: control.control_id,
        control_code: control.control_code,
        control_name: control.control_name,
      }));
    });
  };

  const handleApprove = async (record) => {
    const docId = record.id || record._id;
    const pendingFiles = getPendingFiles(record);
    if (pendingFiles.length === 0) {
      showSnackbar('No pending customized files to approve.', 'info');
      return;
    }

    const filesWithIds = pendingFiles.filter((file) => file.file_id);
    if (filesWithIds.length === 0) {
      showSnackbar('No file IDs found for pending files.', 'error');
      return;
    }

    setApprovingId(docId);
    try {
      for (const file of filesWithIds) {
        await approveCustomizedFileDocumentApi(
          docId,
          file.control_id,
          `Approved by ${userRole.replace('_', ' ')}`,
          file.file_id
        );
      }
      toast.success('Customized document(s) approved successfully.');
      fetchData(pagination.page);
    } catch (error) {
      console.error('Error approving document:', error);
      showSnackbar('Failed to approve document. Please try again.', 'error');
    } finally {
      setApprovingId(null);
    }
  };

  const handleViewFile = async (record) => {
    const allPendingFiles = getPendingFiles(record);
    if (allPendingFiles.length === 0) {
      showSnackbar('No pending files found.', 'info');
      return;
    }

    setPendingFilesToApprove(allPendingFiles);
    setSelectedFileForApproval({ record });
    setCurrentFileIndex(0);

    const file = allPendingFiles[0];
    setDocumentPreviewTitle(file.file_name || file.file?.split('/').pop() || 'File Preview');
    setDocumentPreviewLoading(true);
    setShowDocumentPreviewModal(true);
    try {
      const payload = {
        document_id: record.id || record._id,
        control_id: file.control_id,
        file_id: file.file_id,
        file_type: 'customized',
      };
      // Streamed through the backend and shown from memory (S3 links are blocked in the app).
      setDocumentPreviewFile(await getCustomizedFileBlobApi(payload.document_id, payload.control_id, payload.file_id, payload.file_type));
      setDocumentPreviewUrl('');
    } catch (err) {
      console.error(err);
      setDocumentPreviewUrl('');
      setDocumentPreviewFile(null);
      showSnackbar('Failed to load file preview.', 'error');
    } finally {
      setDocumentPreviewLoading(false);
    }
  };

  const handleApproveFromModal = async () => {
    if (!selectedFileForApproval) return;
    const { record } = selectedFileForApproval;
    const currentFile = pendingFilesToApprove[currentFileIndex];
    if (!currentFile || !currentFile.file_id) {
      showSnackbar('No pending file selected for approval.', 'error');
      return;
    }

    setApprovingId(record.id || record._id);
    try {
      await approveCustomizedFileDocumentApi(
        record.id || record._id,
        currentFile.control_id,
        `Approved by ${userRole.replace('_', ' ')}`,
        currentFile.file_id
      );
      toast.success('Customized document approved successfully.');
      setShowDocumentPreviewModal(false);
      fetchData(pagination.page);
    } catch (error) {
      console.error('Error approving document from modal:', error);
      showSnackbar('Failed to approve document. Please try again.', 'error');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectFromModal = async () => {
    if (!selectedFileForApproval) {
      showSnackbar('No file selected for rejection.', 'error');
      return;
    }
    const { record } = selectedFileForApproval;
    const currentFile = pendingFilesToApprove[currentFileIndex];
    if (!currentFile || !currentFile.file_id) {
      showSnackbar('No pending file selected for rejection.', 'error');
      return;
    }

    setApprovingId(record.id || record._id);
    try {
      await rejectCustomizedFileDocumentApi(
        record.id || record._id,
        currentFile.control_id,
        `Rejected by ${userRole.replace('_', ' ')}`,
        currentFile.file_id
      );
      toast.success('Customized document rejected successfully.');
      setShowDocumentPreviewModal(false);
      fetchData(pagination.page);
    } catch (error) {
      console.error('Error rejecting document:', error);
      showSnackbar('Failed to reject document. Please try again.', 'error');
    } finally {
      setApprovingId(null);
    }
  };

  // Handle file navigation when user clicks prev/next
  useEffect(() => {
    if (pendingFilesToApprove.length > 0 && currentFileIndex >= 0 && currentFileIndex < pendingFilesToApprove.length) {
      const file = pendingFilesToApprove[currentFileIndex];
      setDocumentPreviewTitle(file.file_name || file.file?.split('/').pop() || 'File Preview');
      setDocumentPreviewLoading(true);

      const loadFile = async () => {
        try {
          const { record } = selectedFileForApproval;
          const payload = {
            document_id: record.id || record._id,
            control_id: file.control_id,
            file_id: file.file_id,
            file_type: 'customized',
          };
          // Streamed through the backend and shown from memory (S3 links are blocked in the app).
          setDocumentPreviewFile(await getCustomizedFileBlobApi(payload.document_id, payload.control_id, payload.file_id, payload.file_type));
          setDocumentPreviewUrl('');
        } catch (err) {
          console.error(err);
          setDocumentPreviewUrl('');
          setDocumentPreviewFile(null);
        } finally {
          setDocumentPreviewLoading(false);
        }
      };

      loadFile();
    }
  }, [currentFileIndex, pendingFilesToApprove, selectedFileForApproval]);

  const columns = [
    {
      id: "index",
      header: "#",
      enableSorting: false,
      cell: ({ row }) => <span className="text-gray-500">{(pagination.page - 1) * PAGE_SIZE + row.index + 1}</span>,
    },
    {
      id: "title",
      header: "Title",
      cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.policy_procedure_guideline || 'Untitled document'}</span>,
    },
    {
      id: "framework",
      header: "Framework",
      cell: ({ row }) => <span className="text-gray-600">{formatFramework(row.original)}</span>,
    },
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ getValue }) => <span className="text-gray-600">{getValue() || '—'}</span>,
    },
    {
      accessorKey: "owner",
      header: "Owner",
      cell: ({ getValue }) => <span className="text-gray-600">{getValue() || '—'}</span>,
    },
    {
      id: "pendingFiles",
      header: "Pending Files",
      enableSorting: false,
      cell: ({ row }) => {
        const pendingFiles = getPendingFiles(row.original);
        return pendingFiles.length === 0 ? (
          <span className="text-gray-400">None</span>
        ) : (
          <button
            onClick={() => handleViewFile(row.original)}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline font-medium"
            title="View customized files"
          >
            <EyeIcon className="w-3 h-3" />
            {pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''}
          </button>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Customized File Approval</h2>
          <p className="text-sm text-gray-500 max-w-2xl">
            Pending customized document files uploaded by users. Customer admin and security analyst can review and approve them here.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row items-stretch sm:items-center">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, category, owner…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="relative min-w-[180px]">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={frameworkFilter}
              onChange={(e) => setFrameworkFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Frameworks</option>
              {(frameworks || []).map((fw) => (
                <option key={fw.id || fw.customer_framework_id} value={fw.customer_framework_id || fw.id}>
                  {fw.name}
                </option>
              ))}
            </select>
          </div>
          <div className="relative min-w-[180px]">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {Array.from(new Set((records || []).map((record) => record.category).filter(Boolean))).map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={records}
        getRowId={(record, index) => String(record.id || record._id || index)}
        loading={loading}
        emptyMessage="No pending customized document approvals found."
        manualPagination
        rowCount={pagination.count || 0}
        pagination={{ pageIndex: (pagination.page || 1) - 1, pageSize: PAGE_SIZE }}
        onPaginationChange={(updater) => {
          const current = { pageIndex: (pagination.page || 1) - 1, pageSize: PAGE_SIZE };
          const next = typeof updater === "function" ? updater(current) : updater;
          fetchData(next.pageIndex + 1);
        }}
        enableColumnVisibility={false}
        enableColumnResizing
        maxBodyHeight="65vh"
      />
      <DocumentPreviewModal
        showDocumentPreviewModal={showDocumentPreviewModal}
        setShowDocumentPreviewModal={setShowDocumentPreviewModal}
        documentPreviewTitle={documentPreviewTitle}
        setDocumentPreviewTitle={setDocumentPreviewTitle}
        documentPreviewLoading={documentPreviewLoading}
        setDocumentPreviewLoading={setDocumentPreviewLoading}
        documentPreviewUrl={documentPreviewUrl}
        documentPreviewFile={documentPreviewFile}
        setDocumentPreviewUrl={setDocumentPreviewUrl}
        previewScrollContainerRef={previewScrollContainerRef}
        handlePreviewScroll={() => {}}
        isPreviewScrollCompleted={isPreviewScrollCompleted}
        setIsPreviewScrollCompleted={setIsPreviewScrollCompleted}
        handleAgreeDocument={handleApproveFromModal}
        previewDocumentMeta={previewDocumentMeta}
        setPreviewDocumentMeta={setPreviewDocumentMeta}
        previewPageCount={previewPageCount}
        setPreviewPageCount={setPreviewPageCount}
        previewCurrentPage={previewCurrentPage}
        setPreviewCurrentPage={setPreviewCurrentPage}
        user={null}
        showIAgree={false}
        showApproveReject={true}
        onApprove={handleApproveFromModal}
        onReject={handleRejectFromModal}
        pendingFiles={pendingFilesToApprove}
        currentFileIndex={currentFileIndex}
        setCurrentFileIndex={setCurrentFileIndex}
      />
    </div>
  );
};

export default CustomizedFileApprovalView;
